import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { CONTACT, LOGOS, NAV_LINKS } from "@/lib/site";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  // Anchor links only resolve on the landing page; elsewhere they need the "/" prefix.
  const hrefFor = (h) => (h.startsWith("#") && pathname !== "/" ? `/${h}` : h);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/85 border-b border-[#0B132B]/[0.07] transition-shadow duration-500"
      style={{ boxShadow: scrolled ? "0 12px 36px -20px rgba(11,19,43,0.25)" : "none" }}
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[84px] flex items-center justify-between gap-6">
        <a href={hrefFor("#top")} className="flex items-center shrink-0" data-testid="nav-logo">
          <img src={LOGOS.expante} alt="Expante - Global Consulting" className="h-10 sm:h-11 w-auto" />
        </a>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={hrefFor(l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm font-medium text-[#475569] hover:text-[#5B4199] transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={hrefFor("#contact")}
            data-testid="nav-schedule-btn"
            className="hidden sm:inline-flex items-center gap-1.5 bg-[#0B132B] hover:bg-[#5B4199] text-white text-sm font-semibold rounded-full px-6 py-3 transition-colors duration-300"
          >
            Speak with Expante
            <ArrowUpRight className="h-4 w-4" />
          </a>
          <button
            className="lg:hidden text-[#0B132B] p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden backdrop-blur-xl bg-white/95 border-b border-[#0B132B]/[0.07]"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={hrefFor(l.href)}
                  onClick={() => setOpen(false)}
                  className="text-base font-medium text-[#475569] hover:text-[#5B4199] transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={hrefFor("#contact")}
                onClick={() => setOpen(false)}
                className="inline-flex items-center justify-center gap-1.5 bg-[#0B132B] text-white text-sm font-semibold rounded-full px-5 py-3 mt-2"
              >
                Speak with Expante
                <ArrowUpRight className="h-4 w-4" />
              </a>
              <a href={CONTACT.phoneHref} className="text-sm font-mono-tech text-[#475569] mt-2">
                {CONTACT.phoneDisplay}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
